import { useState } from 'react'
import { CircleArrowUp, Clapperboard, Clock3, Sparkles } from 'lucide-react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { cinemaAssets } from '../config/assets'
import { useSession } from '../state/session-context'

const activationCode = (import.meta.env.VITE_ACTIVATION_CODE || 'L7YU-3MH8-6455-6P29').trim().toUpperCase()

const homeAssetUrls = [
  cinemaAssets.homePaper,
  cinemaAssets.homeTitle,
  cinemaAssets.homeHero,
]
const homePreloadImages: HTMLImageElement[] = []
let homePreloadStarted = false

function preloadHomeAssets() {
  if (homePreloadStarted) return
  homePreloadStarted = true

  for (const src of homeAssetUrls) {
    const image = new Image()
    image.decoding = 'async'
    image.fetchPriority = 'low'
    image.src = src
    homePreloadImages.push(image)
  }

}

export function ActivatePage() {
  const { activated, activate } = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const [code, setCode] = useState('')
  const [error, setError] = useState('')

  if (activated) return <Navigate to="/home" replace />

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const normalized = code.trim().toUpperCase()
    if (!normalized) {
      setError('先输入激活码再开始')
      return
    }
    if (normalized !== activationCode) {
      setError('这个码好像不太对，再检查一下')
      return
    }
    activate()
    const destination = (location.state as { from?: string } | null)?.from ?? '/home'
    navigate(destination, { replace: true })
  }

  return (
    <main className="neutral-page activate-page cinema-cover-page">
      <div className="cinema-page-art" aria-hidden="true">
        <img className="cinema-paper-underlay cinema-paper-underlay--activation" src={cinemaAssets.activationPaper} width="941" height="1672" alt="" decoding="async" fetchPriority="low" />
        <img className="cinema-top-film" src={cinemaAssets.topFilm} width="1000" height="350" alt="" decoding="async" fetchPriority="low" />
        <img className="cinema-stamp" src={cinemaAssets.stamp} width="360" height="240" alt="" decoding="async" fetchPriority="low" />
        <img className="cinema-bottom-decor cinema-bottom-decor--activation" src={cinemaAssets.activationBottom} width="1100" height="620" alt="" decoding="async" fetchPriority="low" />
      </div>
      <section className="page-content activate-content activate-content--replica">
        <h1 className="visually-hidden">你的灵魂电影，会是哪一部？</h1>
        <img
          className="cinema-title-art cinema-title-art--activation"
          src={cinemaAssets.activationTitle}
          width="640"
          height="320"
          alt=""
          aria-hidden="true"
          decoding="sync"
          fetchPriority="high"
        />
        <p className="editorial-quote">
          有些电影只是看过。<br />有些电影，像偷偷写过你。
        </p>
        <div className="facts-line" aria-label="测试说明">
          <span><CircleArrowUp size={14} />28题</span><i />
          <span><Clock3 size={14} />约5分钟</span><i />
          <span><Sparkles size={14} />看看哪部电影最像你</span>
        </div>
        <img
          className="cinema-hero-film cinema-hero-film--activation"
          src={cinemaAssets.activationHero}
          width="760"
          height="428"
          alt=""
          aria-hidden="true"
          decoding="async"
          fetchPriority="auto"
        />
        <form className="activation-card" onSubmit={submit} noValidate>
          <div className="section-kicker"><Sparkles size={18} /> 输入你的测试激活码</div>
          <label className={`activation-input${error ? ' activation-input--error' : ''}`}>
            <Clapperboard size={24} aria-hidden="true" />
            <input
              value={code}
              onFocus={preloadHomeAssets}
              onChange={(event) => {
                preloadHomeAssets()
                setCode(event.target.value)
                if (error) setError('')
              }}
              placeholder="请输入激活码"
              autoCapitalize="characters"
              autoComplete="one-time-code"
              aria-describedby="activation-error"
            />
          </label>
          <button className="primary-button" type="submit">
            开始测试 <Sparkles size={20} aria-hidden="true" />
          </button>
          <p id="activation-error" className="field-message" aria-live="polite">
            {error || '— 激活后即可开始匹配 —'}
          </p>
        </form>
      </section>
    </main>
  )
}
